import React from 'react';
import { AppShell } from '../components/Layout';
import SeoHead from '../components/seo/SeoHead';

/**
 * PrivacyPage — GDPR + UK PECR compliant privacy notice.
 *
 * NOTE: Starter draft. Replace with the final notice reviewed by counsel
 * before production launch.
 */
export default function PrivacyPage() {
  return (
    <AppShell>
      <SeoHead
        title={"Privacy Policy — Blayzrr"} 
        description={"How Blayzrr collects, uses, and protects your data. GDPR-aligned, LinkedIn-safe."} 
        path={"/privacy"} 
      />
      <main className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20" data-testid="privacy-page">
        <p className="text-[11px] font-mono-edge tracking-[0.22em] uppercase text-primary">Legal</p>
        <h1 className="mt-3 font-display font-semibold text-4xl sm:text-5xl tracking-[-0.04em]">
          Privacy Policy
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: 28 June 2026 · GDPR &amp; UK PECR compliant
        </p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/90">
          <Section title="1. Who controls your data">
            Blayzrr is the data controller for personal data processed via blayzrr.com. You can reach the Data Protection contact at <a className="text-primary hover:underline" href="mailto:help@blayzrr.com">help@blayzrr.com</a>.
          </Section>
          <Section title="2. What we collect">
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Account data:</strong> email, hashed password, full name (if provided), country.</li>
              <li><strong>LinkedIn audit input:</strong> profile URL, uploaded PDF, fields you type manually.</li>
              <li><strong>Generated outputs:</strong> audits, rewrites, posts, carousels you create.</li>
              <li><strong>Billing:</strong> Razorpay tokens — we never see your card number.</li>
              <li><strong>Telemetry:</strong> anonymised page-view / click data, only after cookie consent.</li>
              <li><strong>Consent log:</strong> IP, user-agent, timestamp, choice (Accept / Reject) for GDPR Art. 7(3) audit trail.</li>
            </ul>
          </Section>
          <Section title="3. Legal bases">
            We rely on: <em>contract</em> (Art. 6(1)(b)) to deliver the service you paid for; <em>legitimate interests</em> (Art. 6(1)(f)) for security and fraud prevention; <em>consent</em> (Art. 6(1)(a)) for marketing analytics; <em>legal obligation</em> (Art. 6(1)(c)) for tax / invoicing.
          </Section>
          <Section title="4. How long we keep it">
            Account + audit data: until you delete your account, then 30 days in encrypted backup before purge. Billing records: 7 years (legal). Telemetry: 13 months, then purged or anonymised.
          </Section>
          <Section title="5. Sub-processors">
            We use: Anthropic (Claude) and OpenAI for LLM inference, Apify for LinkedIn scraping (paid plans only), Razorpay for payments, and a managed MongoDB host. All sub-processors are under written DPAs.
          </Section>
          <Section title="6. International transfers">
            Some processors are based outside the EEA / UK. Transfers rely on the EU SCCs + UK Addendum where applicable.
          </Section>
          <Section title="7. Your rights">
            You have the right to access, rectify, port, restrict, and delete your data — plus the right to withdraw consent at any time (as easy as giving it, GDPR Art. 7(3)). Most rights are self-serve in Settings; otherwise email <a className="text-primary hover:underline" href="mailto:help@blayzrr.com">help@blayzrr.com</a>.
          </Section>
          <Section title="8. Cookies">
            Strictly-necessary cookies (auth, CSRF) are set without consent. All other cookies (analytics, marketing) only load after you click <strong>Accept</strong> on the consent banner. You can withdraw at any time via <em>Cookie preferences</em>.
          </Section>
          <Section title="9. Complaints">
            If you believe we’ve mishandled your data, you can lodge a complaint with the ICO (UK) or your local EU supervisory authority. We’d appreciate the chance to fix it first — please email us.
          </Section>
        </div>
      </main>
    </AppShell>
  );
}

const Section = ({ title, children }) => (
  <section>
    <h2 className="font-display font-semibold text-xl sm:text-2xl tracking-[-0.02em]">
      {title}
    </h2>
    <div className="mt-3 text-foreground/85">{children}</div>
  </section>
);
